import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Localization } from "@viamap/viamap2-common";
import {
   aiFieldTone,
   formatAiFieldStatus,
   formatProvenanceChip,
   provenanceChipTone,
   type ANNServitutProvenance,
   type ServitutAiFieldStatus,
} from "src/modules/ANNModule/ANNServitutProvenance";
import type { ServitutDocumentExtent, ServitutPageSourceStatus } from "./ServitutRedegoerelseRow";

export function AiRobotIcon() {
   return (
      <svg
         className="servitut-redegoerelse__aiRobot"
         width="14"
         height="14"
         viewBox="0 0 16 16"
         fill="none"
         aria-hidden="true"
         focusable="false"
      >
         <rect x="3" y="5.2" width="10" height="8.3" rx="2" stroke="currentColor" strokeWidth="1.35" />
         <path d="M8 5.2V2.6" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" />
         <circle cx="8" cy="2.15" r="1.05" stroke="currentColor" strokeWidth="1.2" />
         <circle cx="6.15" cy="8.75" r="1.05" fill="currentColor" />
         <circle cx="9.85" cy="8.75" r="1.05" fill="currentColor" />
         <path d="M6.2 11.45h3.6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
   );
}

export function PersonIcon() {
   return (
      <svg
         className="servitut-redegoerelse__personIcon"
         width="14"
         height="14"
         viewBox="0 0 16 16"
         fill="none"
         aria-hidden="true"
         focusable="false"
      >
         <circle cx="8" cy="5.15" r="2.35" stroke="currentColor" strokeWidth="1.35" />
         <path
            d="M3.4 13.15c.55-2.55 2.2-3.9 4.6-3.9s4.05 1.35 4.6 3.9"
            stroke="currentColor"
            strokeWidth="1.35"
            strokeLinecap="round"
         />
      </svg>
   );
}

export function ServitutMarkChip(props: {
   text: string;
   tone?: "ok" | "warn" | "bad" | "neutral";
   title?: string;
   showAi?: boolean;
   showPerson?: boolean;
}) {
   if (!props.text) {
      return null;
   }
   const markClass = props.showPerson
      ? " servitut-redegoerelse__markChip--person"
      : props.showAi
         ? " servitut-redegoerelse__markChip--ai"
         : "";
   return (
      <span
         className={`servitut-redegoerelse__markChip servitut-redegoerelse__markChip--${props.tone || "neutral"}${markClass}`}
         title={props.title || props.text}
         aria-label={props.showPerson || props.showAi ? props.text : undefined}
      >
         {props.showPerson ? <PersonIcon /> : props.showAi ? <AiRobotIcon /> : null}
         {props.text}
      </span>
   );
}

export function ServitutProvenanceChip(props: {
   provenance?: ANNServitutProvenance;
   hideAiGenerated?: boolean;
   hideFetchedFromAkt?: boolean;
}) {
   const text = formatProvenanceChip(props.provenance, {
      hideAiGenerated: props.hideAiGenerated,
      hideFetchedFromAkt: props.hideFetchedFromAkt,
   });
   if (!text) {
      return null;
   }
   return <ServitutMarkChip text={text} tone={provenanceChipTone(props.provenance)} />;
}

export function ServitutDigitalDocumentChip() {
   return (
      <ServitutMarkChip
         text={Localization.getText("Digital document")}
         tone="ok"
      />
   );
}

export function ServitutPageFindChip(props: { source: ServitutPageSourceStatus }) {
   if (props.source.kind === "hidden") {
      return null;
   }
   if (props.source.kind === "user_pages") {
      return (
         <ServitutMarkChip
            text={Localization.getFormattedText("User found {count} pages", { count: props.source.count })}
            tone="ok"
            showPerson
         />
      );
   }
   if (props.source.kind === "ai_pages") {
      if (!props.source.confident) {
         return (
            <ServitutMarkChip
               text={Localization.getFormattedText("AI found {count} pages, not confident", { count: props.source.count })}
               tone="warn"
               title={props.source.reason}
               showAi
            />
         );
      }
      return (
         <ServitutMarkChip
            text={Localization.getFormattedText("AI found {count} pages", { count: props.source.count })}
            tone="ok"
            title={props.source.reason}
            showAi
         />
      );
   }
   if (props.source.kind === "ai_none") {
      return (
         <ServitutMarkChip
            text={Localization.getText("AI found no pages")}
            tone="bad"
            showAi
         />
      );
   }
   return (
      <ServitutMarkChip
         text={Localization.getText("Page search not run")}
         tone="neutral"
         showAi
      />
   );
}

export function ServitutDocumentExtentChip(props: {
   extent: ServitutDocumentExtent;
}) {
   if (props.extent.kind !== "entire_akt") {
      return null;
   }
   return (
      <ServitutMarkChip
         text={Localization.getText("Entire akt")}
         tone="neutral"
      />
   );
}

export function ServitutImportedChip() {
   return (
      <ServitutMarkChip
         text={Localization.getText("Annotation imported")}
         tone="ok"
         title={Localization.getText("Using saved annotation PDF")}
      />
   );
}

export function ServitutAiFieldChip(props: { status?: ServitutAiFieldStatus; text?: string }) {
   const text = props.text || formatAiFieldStatus(props.status);
   return (
      <ServitutMarkChip
         text={text}
         tone={aiFieldTone(props.status)}
         showAi={props.status !== "person_approved" && props.status !== "not_run"}
      />
   );
}

export function ServitutSourceHint(props: {
   mark: string;
   text: string;
   tone: "ovrig" | "text" | "annotation" | "rigsarkiv" | "missing";
}) {
   const wrapRef = useRef<HTMLSpanElement | null>(null);
   const [open, setOpen] = useState(false);
   const [pos, setPos] = useState({ top: 0, left: 0 });
   const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

   const clearHideTimer = () => {
      if (hideTimerRef.current !== null) {
         clearTimeout(hideTimerRef.current);
         hideTimerRef.current = null;
      }
   };

   const updatePosition = () => {
      const el = wrapRef.current;
      if (!el) {
         return;
      }
      const r = el.getBoundingClientRect();
      setPos({ top: r.bottom + 8, left: r.left + r.width / 2 });
   };

   const openPopover = () => {
      clearHideTimer();
      updatePosition();
      setOpen(true);
   };

   const scheduleClose = () => {
      clearHideTimer();
      hideTimerRef.current = setTimeout(() => setOpen(false), 80);
   };

   useLayoutEffect(() => {
      if (!open) {
         return;
      }
      updatePosition();
   }, [open, props.text]);

   useEffect(() => {
      if (!open) {
         return;
      }
      const close = () => setOpen(false);
      window.addEventListener("scroll", close, true);
      window.addEventListener("resize", close);
      return () => {
         window.removeEventListener("scroll", close, true);
         window.removeEventListener("resize", close);
      };
   }, [open]);

   useEffect(() => () => clearHideTimer(), []);

   return (
      <>
         <span
            ref={wrapRef}
            className={`servitut-redegoerelse__sourceHint servitut-redegoerelse__sourceHint--${props.tone}`}
            aria-label={props.text}
            onMouseEnter={openPopover}
            onMouseLeave={scheduleClose}
         >
            {props.mark}
         </span>
         {open ? createPortal(
            <div
               className="servitut-redegoerelse__sourceHintPopover"
               style={{ top: pos.top, left: pos.left }}
               role="tooltip"
               onMouseEnter={clearHideTimer}
               onMouseLeave={scheduleClose}
            >
               {props.text}
            </div>,
            document.body,
         ) : null}
      </>
   );
}
